import { getStudentBill, calculateTotalFeesDue as calculateTotalFeesDueUtil, getOtherChargesFromBill } from '../utils/billUtils';
import {
  isPaymentCancelled,
  isPaymentPendingCancellation,
  isPaymentCountedInTotals,
  paymentStatusLabel,
} from '../utils/schoolFeePayments';

class SchoolFeesDataService {
  // Helper function to safely format numbers
  formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
    return parseFloat(value).toLocaleString();
  }

  // Calculate total amount paid from payment array
  // EXACT match to school fees page totalamountpaid function (tableschoolfees.js line 2984)
  totalAmountPaid(payments) {
    if (!payments || !Array.isArray(payments)) return 0;
    
    // EXACT match: school fees page totalamountpaid includes ALL payments
    // No discount exclusion - it sums everything to match the displayed amount
    // This matches: totalamountpaid(row.finance[row.finance.length-1].payment)
    var result = 0;
    for (var i in payments) {
      if (!isPaymentCountedInTotals(payments[i])) continue;
      result += parseFloat(payments[i].amountpaid) || 0;
    }
    return result;
  }

  // Calculate total fees due for a student
  // Updated to use individual bills when available
  calculateTotalFeesDue(student, classFees, classData = null, term = null) {
    // If we have classData and term, use the bill utility to get the correct bill
    if (classData && term) {
      return calculateTotalFeesDueUtil(student, classData, term);
    }

    // Fallback to original logic if classData/term not provided
    if (!student.finance || student.finance.length === 0) {
      // If no finance record, use class fees
      return parseFloat(classFees) || 0;
    }

    const latestFinance = student.finance[student.finance.length - 1];
    const totalFees = parseFloat(latestFinance.totalfees) || 0;
    const arrears = parseFloat(latestFinance.Arrears) || 0;
    
    return totalFees + arrears;
  }

  // Calculate total amount paid from ALL finance records (for accounting reports)
  // This sums all payments from all terms to get the exact total collected
  calculateTotalAmountPaidAllTime(student) {
    if (!student.finance || student.finance.length === 0) return 0;
    
    let totalPaid = 0;
    // Loop through ALL finance records (all terms)
    student.finance.forEach(financeRecord => {
      if (financeRecord.payment && Array.isArray(financeRecord.payment)) {
        // Sum all payments from this finance record
        totalPaid += this.totalAmountPaid(financeRecord.payment);
      }
    });
    
    return totalPaid;
  }

  // Calculate amount paid by student
  // EXACT match to school fees page logic (tableschoolfees.js line 5801)
  // Only shows amount paid if latest finance record term matches mainterm
  // Uses: totalamountpaid(row.finance[row.finance.length-1].payment)
  calculateAmountPaid(student, mainterm = null) {
    if (!student.finance || student.finance.length === 0) return 0;
    
    // Use latest finance record (matching: finance[finance.length-1])
    const latestFinance = student.finance[student.finance.length - 1];
    if (!latestFinance.payment || !Array.isArray(latestFinance.payment)) return 0;
    
    // EXACT match to school fees page: Only show if term === mainterm
    // If mainterm is provided and doesn't match, return 0 (like "No data")
    // If mainterm is "none" or null, show all (for accounting reports when no term is selected)
    if (mainterm !== null && mainterm !== undefined && mainterm !== "none") {
      // Handle term comparison - term might be string or object
      let latestTerm = latestFinance.term;
      if (typeof latestTerm === 'object' && latestTerm !== null) {
        latestTerm = latestTerm.name || latestTerm.term || latestTerm.value || String(latestTerm);
      }
      // Convert both to strings for comparison (case-insensitive for robustness)
      if (String(latestTerm).trim() !== String(mainterm).trim()) {
        return 0; // No data - term doesn't match (like school fees page shows "No data")
      }
    }
    
    // EXACT match to school fees page: totalamountpaid(payment)
    // This sums ALL payments in the array (including discounts)
    return this.totalAmountPaid(latestFinance.payment);
  }

  // Calculate outstanding balance for student
  calculateOutstandingBalance(student, classFees, mainterm = null) {
    const totalDue = this.calculateTotalFeesDue(student, classFees);
    const amountPaid = this.calculateAmountPaid(student, mainterm);
    return Math.max(0, totalDue - amountPaid);
  }

  // Get school fees data for accounting reports
  // Matches the logic from school fees page - shows all students but filters payments by date range
  // mainterm: The current term (like mainterm in school fees page) - only shows amount paid if term matches
  async getSchoolFeesData(students, classData, startDate, endDate, mainterm = null) {
    // Normalize dates to start/end of day for comparison
    const normalizedStartDate = new Date(startDate);
    normalizedStartDate.setHours(0, 0, 0, 0);
    const normalizedEndDate = new Date(endDate);
    normalizedEndDate.setHours(23, 59, 59, 999);

    // Process ALL students (not filtered by date) - similar to school fees page
    // But filter payments by date range for the period report
    let totalFeesDue = 0;
    let totalAmountPaid = 0;
    let totalAmountPaidInPeriod = 0; // Payments within date range
    let totalDiscounts = 0;
    let totalDiscountsInPeriod = 0;
    let totalOutstanding = 0;
    let paidStudents = 0;
    let outstandingStudents = 0;
    let totalOnlineFeePaidAllTime = 0;
    let totalOnlineFeePaidInPeriod = 0;
    let totalInPersonFeePaidAllTime = 0;
    let totalInPersonFeePaidInPeriod = 0;

    const studentFeesData = [];
    const isDiscountPayment = (payment) =>
      String(payment?.des || '').trim().toLowerCase() === 'discount';
    const isOnlineFeePayment = (payment) =>
      String(payment?.paymentMethod || '').trim().toLowerCase() === 'online';
    const parsePaymentDateMs = (dateValue) => {
      const raw = String(dateValue || '').trim();
      if (!raw) return NaN;
      const ymd = raw.includes('T') ? raw.split('T')[0] : raw;
      const parts = ymd.split('-');
      if (parts.length < 3) return NaN;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return NaN;
      return new Date(year, month - 1, day, 12, 0, 0, 0).getTime();
    };

    students.forEach(student => {
      // Find class fees for this student
      const studentClass = classData.find(cls => cls.classname === student.class_);
      const classFees = studentClass ? parseFloat(studentClass.classfees) : 0;

      // Calculate total fees due (all time)
      const totalDue = this.calculateTotalFeesDue(student, classFees);
      
      // For accounting reports (when mainterm is null or "none"), use ALL payments from ALL terms
      // For school fees page (when mainterm is specified), use only latest finance record matching that term
      let amountPaid = 0;
      if (mainterm === null || mainterm === undefined || mainterm === "none") {
        // Accounting report: Sum ALL payments from ALL finance records to get exact total collected
        amountPaid = this.calculateTotalAmountPaidAllTime(student);
      } else {
        // School fees page: Only show amount paid if latest finance record term matches mainterm
        amountPaid = this.calculateAmountPaid(student, mainterm);
      }
      
      // Calculate amount paid in the selected period (filtered by date range)
      // For period reports, we filter by date but still include all payments (matching school fees logic)
      let amountPaidInPeriod = 0;
      let discountAmountAllTime = 0;
      let discountAmountInPeriod = 0;
      let studentOnlineFeePaidAllTime = 0;
      let studentOnlineFeePaidInPeriod = 0;
      let studentInPersonFeePaidAllTime = 0;
      let studentInPersonFeePaidInPeriod = 0;
      if (student.finance && student.finance.length > 0) {
        student.finance.forEach(financeRecord => {
          if (financeRecord.payment && Array.isArray(financeRecord.payment)) {
            financeRecord.payment.forEach(payment => {
              if (!isPaymentCountedInTotals(payment)) return;
              if (isDiscountPayment(payment)) {
                discountAmountAllTime += parseFloat(payment.amountpaid) || 0;
              }
              // Include ALL payments (including discounts) for period calculation
              // This matches the school fees page logic where all payments are counted
              
              // Check if payment is within date range
              if (payment.date) {
                const paymentDateMs = parsePaymentDateMs(payment.date);
                if (Number.isFinite(paymentDateMs)) {
                  if (normalizedStartDate.getTime() <= paymentDateMs && paymentDateMs <= normalizedEndDate.getTime()) {
                    amountPaidInPeriod += parseFloat(payment.amountpaid) || 0;
                    if (isDiscountPayment(payment)) {
                      discountAmountInPeriod += parseFloat(payment.amountpaid) || 0;
                    }
                  }
                }
              }

              if (!isDiscountPayment(payment)) {
                const feeAmt = parseFloat(payment.amountpaid) || 0;
                if (isOnlineFeePayment(payment)) {
                  studentOnlineFeePaidAllTime += feeAmt;
                  totalOnlineFeePaidAllTime += feeAmt;
                } else {
                  studentInPersonFeePaidAllTime += feeAmt;
                  totalInPersonFeePaidAllTime += feeAmt;
                }
                if (payment.date) {
                  const paymentDateMs = parsePaymentDateMs(payment.date);
                  if (
                    Number.isFinite(paymentDateMs) &&
                    normalizedStartDate.getTime() <= paymentDateMs &&
                    paymentDateMs <= normalizedEndDate.getTime()
                  ) {
                    if (isOnlineFeePayment(payment)) {
                      studentOnlineFeePaidInPeriod += feeAmt;
                      totalOnlineFeePaidInPeriod += feeAmt;
                    } else {
                      studentInPersonFeePaidInPeriod += feeAmt;
                      totalInPersonFeePaidInPeriod += feeAmt;
                    }
                  }
                }
              }
            });
          }
        });
      }
      
      // Calculate outstanding balance
      // For accounting reports, outstanding should be based on all-time payments
      const outstanding = Math.max(0, totalDue - amountPaid);

      // Add to totals
      totalFeesDue += totalDue;
      totalAmountPaid += amountPaid;
      totalAmountPaidInPeriod += amountPaidInPeriod;
      totalDiscounts += discountAmountAllTime;
      totalDiscountsInPeriod += discountAmountInPeriod;
      totalOutstanding += outstanding;

      // Count students
      if (outstanding === 0) {
        paidStudents++;
      } else {
        outstandingStudents++;
      }

      // Store individual student data
      studentFeesData.push({
        studentId: student._id || student.ID,
        studentName: `${student.lastname || ''} ${student.othernames || ''}`.trim(),
        studentIdNumber: student.ID || student._id,
        class: student.class_ || 'Unknown',
        status: student.status || 'Active',
        totalFeesDue: totalDue,
        amountPaid: amountPaid,
        amountPaidInPeriod: amountPaidInPeriod, // Payments in selected period
        discountsGiven: discountAmountAllTime,
        discountsGivenInPeriod: discountAmountInPeriod,
        outstandingBalance: outstanding,
        finance: student.finance || [],
        isFullyPaid: outstanding === 0,
        onlineFeePaidAllTime: studentOnlineFeePaidAllTime,
        onlineFeePaidInPeriod: studentOnlineFeePaidInPeriod,
        inPersonFeePaidAllTime: studentInPersonFeePaidAllTime,
        inPersonFeePaidInPeriod: studentInPersonFeePaidInPeriod,
        paymentHistory: this.getPaymentHistory(student)
      });
    });

    // Calculate collection rate (all time)
    const collectionRate = totalFeesDue > 0 ? (totalAmountPaid / totalFeesDue) * 100 : 0;
    
    // Calculate collection rate for the period
    const periodCollectionRate = totalFeesDue > 0 ? (totalAmountPaidInPeriod / totalFeesDue) * 100 : 0;

    const periodPaymentData = this.collectPeriodFeePayments(
      students,
      normalizedStartDate,
      normalizedEndDate
    );

    return {
      students: studentFeesData,
      periodPayments: periodPaymentData.periodPayments,
      cancelledPaymentsInPeriod: periodPaymentData.cancelledPaymentsInPeriod,
      summary: {
        totalStudents: students.length,
        paidStudents: paidStudents,
        outstandingStudents: outstandingStudents,
        totalFeesDue: totalFeesDue,
        totalAmountPaid: totalAmountPaid, // All time
        totalAmountPaidInPeriod: totalAmountPaidInPeriod, // In selected period
        totalDiscounts: totalDiscounts,
        totalDiscountsInPeriod: totalDiscountsInPeriod,
        totalOnlineFeePaidAllTime: totalOnlineFeePaidAllTime,
        totalOnlineFeePaidInPeriod: totalOnlineFeePaidInPeriod,
        totalInPersonFeePaidAllTime: totalInPersonFeePaidAllTime,
        totalInPersonFeePaidInPeriod: totalInPersonFeePaidInPeriod,
        totalCancelledAllTime: periodPaymentData.totalCancelledAllTime,
        totalCancelledInPeriod: periodPaymentData.totalCancelledInPeriod,
        totalOutstanding: totalOutstanding,
        collectionRate: collectionRate, // All time
        periodCollectionRate: periodCollectionRate, // For selected period
        averageFeesPerStudent: students.length > 0 ? totalFeesDue / students.length : 0,
        period: {
          start: normalizedStartDate.toISOString().split('T')[0],
          end: normalizedEndDate.toISOString().split('T')[0]
        }
      },
      breakdown: {
        byClass: this.getBreakdownByClass(studentFeesData),
        byStatus: this.getBreakdownByStatus(studentFeesData),
        byPaymentStatus: this.getBreakdownByPaymentStatus(studentFeesData),
        byTerm: this.getBreakdownByTerm(studentFeesData, normalizedStartDate, normalizedEndDate)
      }
    };
  }

  // Get breakdown by term
  getBreakdownByTerm(studentFeesData, startDate, endDate) {
    const breakdown = {};
    
    studentFeesData.forEach(student => {
      if (student.finance && Array.isArray(student.finance)) {
        student.finance.forEach(financeRecord => {
          // Fix term - convert object to string if needed
          let termString = 'Unknown';
          if (financeRecord.term) {
            if (typeof financeRecord.term === 'string') {
              termString = financeRecord.term;
            } else if (typeof financeRecord.term === 'object') {
              termString = financeRecord.term.name || 
                           financeRecord.term.term || 
                           financeRecord.term.value || 
                           JSON.stringify(financeRecord.term);
            } else {
              termString = String(financeRecord.term);
            }
          }
          
          if (!breakdown[termString]) {
            breakdown[termString] = {
              totalStudents: 0,
              totalFeesDue: 0,
              totalAmountPaid: 0,
              totalAmountPaidInPeriod: 0,
              totalOutstanding: 0
            };
          }
          
          // Calculate payments in period for this term
          let termPaymentsInPeriod = 0;
          if (financeRecord.payment && Array.isArray(financeRecord.payment)) {
            financeRecord.payment.forEach(payment => {
              if (!isPaymentCountedInTotals(payment)) return;
              // Include all payments (no discount exclusion) to match school fees page
              if (payment.date) {
                const paymentDate = new Date(payment.date);
                if (!isNaN(paymentDate.getTime())) {
                  const normalizedPaymentDate = new Date(paymentDate);
                  normalizedPaymentDate.setHours(0, 0, 0, 0);
                  if (normalizedPaymentDate >= startDate && normalizedPaymentDate <= endDate) {
                    termPaymentsInPeriod += parseFloat(payment.amountpaid) || 0;
                  }
                }
              }
            });
          }
          
          breakdown[termString].totalStudents++;
          breakdown[termString].totalFeesDue += student.totalFeesDue;
          breakdown[termString].totalAmountPaid += student.amountPaid;
          breakdown[termString].totalAmountPaidInPeriod += termPaymentsInPeriod;
          breakdown[termString].totalOutstanding += student.outstandingBalance;
        });
      }
    });
    
    return breakdown;
  }

  // Get breakdown by class
  getBreakdownByClass(studentFeesData) {
    const breakdown = {};
    
    studentFeesData.forEach(student => {
      const className = student.class;
      if (!breakdown[className]) {
        breakdown[className] = {
          totalStudents: 0,
          totalFeesDue: 0,
          totalAmountPaid: 0,
          totalOutstanding: 0,
          paidStudents: 0,
          outstandingStudents: 0
        };
      }
      
      breakdown[className].totalStudents++;
      breakdown[className].totalFeesDue += student.totalFeesDue;
      breakdown[className].totalAmountPaid += student.amountPaid;
      breakdown[className].totalOutstanding += student.outstandingBalance;
      
      if (student.isFullyPaid) {
        breakdown[className].paidStudents++;
      } else {
        breakdown[className].outstandingStudents++;
      }
    });

    return breakdown;
  }

  // Get breakdown by student status
  getBreakdownByStatus(studentFeesData) {
    const breakdown = {};
    
    studentFeesData.forEach(student => {
      const status = student.status;
      if (!breakdown[status]) {
        breakdown[status] = {
          totalStudents: 0,
          totalFeesDue: 0,
          totalAmountPaid: 0,
          totalOutstanding: 0
        };
      }
      
      breakdown[status].totalStudents++;
      breakdown[status].totalFeesDue += student.totalFeesDue;
      breakdown[status].totalAmountPaid += student.amountPaid;
      breakdown[status].totalOutstanding += student.outstandingBalance;
    });

    return breakdown;
  }

  // Get breakdown by payment status
  getBreakdownByPaymentStatus(studentFeesData) {
    const breakdown = {
      fullyPaid: {
        count: 0,
        totalFees: 0,
        totalPaid: 0
      },
      partiallyPaid: {
        count: 0,
        totalFees: 0,
        totalPaid: 0,
        totalOutstanding: 0
      },
      notPaid: {
        count: 0,
        totalFees: 0,
        totalOutstanding: 0
      }
    };
    
    studentFeesData.forEach(student => {
      if (student.isFullyPaid) {
        breakdown.fullyPaid.count++;
        breakdown.fullyPaid.totalFees += student.totalFeesDue;
        breakdown.fullyPaid.totalPaid += student.amountPaid;
      } else if (student.amountPaid > 0) {
        breakdown.partiallyPaid.count++;
        breakdown.partiallyPaid.totalFees += student.totalFeesDue;
        breakdown.partiallyPaid.totalPaid += student.amountPaid;
        breakdown.partiallyPaid.totalOutstanding += student.outstandingBalance;
      } else {
        breakdown.notPaid.count++;
        breakdown.notPaid.totalFees += student.totalFeesDue;
        breakdown.notPaid.totalOutstanding += student.outstandingBalance;
      }
    });

    return breakdown;
  }

  // Flatten fee payments in date range; split active vs cancelled (excl. discounts)
  collectPeriodFeePayments(students, normalizedStartDate, normalizedEndDate) {
    const isDiscountPayment = (payment) =>
      String(payment?.des || '').trim().toLowerCase() === 'discount';
    const parsePaymentDateMs = (dateValue) => {
      const raw = String(dateValue || '').trim();
      if (!raw) return NaN;
      const ymd = raw.includes('T') ? raw.split('T')[0] : raw;
      const parts = ymd.split('-');
      if (parts.length < 3) return NaN;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return NaN;
      return new Date(year, month - 1, day, 12, 0, 0, 0).getTime();
    };

    const periodPayments = [];
    const cancelledPaymentsInPeriod = [];
    let totalCancelledAllTime = 0;
    let totalCancelledInPeriod = 0;
    const startMs = normalizedStartDate.getTime();
    const endMs = normalizedEndDate.getTime();

    (students || []).forEach((student) => {
      if (!student?.finance?.length) return;
      const studentName = `${student.lastname || ''} ${student.othernames || ''}`.trim();
      student.finance.forEach((financeRecord) => {
        if (!financeRecord?.payment?.length) return;
        financeRecord.payment.forEach((payment) => {
          if (!payment || isDiscountPayment(payment)) return;

          const amt = parseFloat(payment.amountpaid) || 0;
          const cancelled = isPaymentCancelled(payment);
          const paymentDateMs = parsePaymentDateMs(payment.date);
          const inPeriod =
            Number.isFinite(paymentDateMs) && startMs <= paymentDateMs && paymentDateMs <= endMs;

          const row = {
            studentId: student.ID || student._id,
            studentName,
            class: student.class_ || 'Unknown',
            amount: amt,
            date: payment.date,
            paidTo: payment.receiver || '',
            paymentChannel:
              String(payment.paymentMethod || '').trim().toLowerCase() === 'online'
                ? 'online'
                : 'in_person',
            statusLabel: paymentStatusLabel(payment),
            isCancelled: cancelled,
            isPendingCancellation: isPaymentPendingCancellation(payment),
          };

          if (cancelled) {
            totalCancelledAllTime += amt;
            if (inPeriod) {
              totalCancelledInPeriod += amt;
              cancelledPaymentsInPeriod.push(row);
            }
          } else if (inPeriod) {
            periodPayments.push(row);
          }
        });
      });
    });

    return {
      periodPayments,
      cancelledPaymentsInPeriod,
      totalCancelledAllTime,
      totalCancelledInPeriod,
    };
  }

  // Get payment history for a specific student (active + pending only)
  getPaymentHistory(student) {
    if (!student.finance || student.finance.length === 0) return [];
    
    const allPayments = [];
    student.finance.forEach(finance => {
      if (finance.payment && Array.isArray(finance.payment)) {
        finance.payment.forEach(payment => {
          const description = payment.des || 'Payment';
          const discountRow =
            String(description).trim().toLowerCase() === 'discount';
          const cancelled = isPaymentCancelled(payment);
          allPayments.push({
            date: payment.date,
            amount: parseFloat(payment.amountpaid) || 0,
            description,
            term: finance.term,
            receiptNumber: payment.receiptNumber || null,
            paymentMethod: payment.paymentMethod || null,
            statusLabel: paymentStatusLabel(payment),
            isCancelled: cancelled,
            isOnlineFeePayment:
              !discountRow &&
              !cancelled &&
              String(payment?.paymentMethod || '').trim().toLowerCase() === 'online'
          });
        });
      }
    });
    
    // Sort by date (newest first)
    return allPayments.sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  // Get monthly collection data
  getMonthlyCollectionData(studentFeesData, startDate, endDate) {
    const monthlyData = {};
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      monthlyData[monthKey] = {
        month: currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        totalCollected: 0,
        totalStudents: 0,
        newPayments: 0
      };
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    studentFeesData.forEach(student => {
      const paymentHistory = this.getPaymentHistory(student);
      paymentHistory.forEach(payment => {
        if (payment.isCancelled) return;
        const paymentDate = new Date(payment.date);
        if (paymentDate >= startDate && paymentDate <= endDate) {
          const monthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
          if (monthlyData[monthKey]) {
            monthlyData[monthKey].totalCollected += payment.amount;
            monthlyData[monthKey].newPayments++;
          }
        }
      });
    });
    
    return Object.values(monthlyData);
  }
}

export default new SchoolFeesDataService(); 